import { useMutation, useQuery } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import {
  AppliedFilters,
  Button,
  Container,
  Table,
  useModal,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import { isEmpty, isNil, isUndefined } from "lodash";
import { StrictMode, useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ActionButtons } from "./action-buttons";
import { AdditionModal } from "./addition-modal";
import { GET_TOE_PORTS, UPDATE_TOE_PORT } from "./queries";
import type {
  IGroupToePortsProps,
  IToePortAttr,
  IToePortData,
  IToePortEdge,
} from "./types";
import {
  formatToePortData,
  getColumns,
  handleUpdateToePortError,
  isEqualRootId,
} from "./utils";

import { useToEPortsFilters } from "features/toes-ports/filters";
import type {
  GetToePortsQueryVariables,
  ToePortEdge,
  ToePortsFiltersInput,
  ToePortsSort,
  ToePortsSortInput,
  UpdateToePortMutation,
} from "gql/graphql";
import { useAudit, useAuthz, useDebouncedCallback } from "hooks";
import { useTable } from "hooks/use-table";
import { getErrors } from "utils/helpers";
import { Logger } from "utils/logger";
import { msgSuccess } from "utils/notifications";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

const DEBOUNCE_DELAY = 300;

const GroupToePorts: FC<IGroupToePortsProps> = ({
  isInternal,
}): JSX.Element => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();
  const { groupName } = useParams() as { groupName: string };
  const { canMutate, canResolve } = useAuthz();
  const tableHelpers = useTableServerSideStore();
  const canGetAttackedAt = canResolve("toe_port_attacked_at");
  const canGetAttackedBy = canResolve("toe_port_attacked_by");
  const canGetBePresentUntil = canResolve("toe_port_be_present_until");
  const canGetFirstAttackAt = canResolve("toe_port_first_attack_at");
  const canGetSeenFirstTimeBy = canResolve("toe_port_seen_first_time_by");
  const canUpdateToePort = canMutate("update_toe_port");

  const additionModalProps = useModal("addition-modal");
  const [isMarkingAsAttacked, setIsMarkingAsAttacked] = useState(false);
  const [filteredDataset, setFilteredDataset] = useState<IToePortData[]>([]);
  const [toePortsFilters, setToePortsFilters] = useState<ToePortsFiltersInput>(
    {},
  );

  const [handleUpdateToePort] = useMutation(UPDATE_TOE_PORT, {
    onError: handleUpdateToePortError,
  });

  const getToePortsVariables = useMemo(
    (): GetToePortsQueryVariables => ({
      canGetAttackedAt,
      canGetAttackedBy,
      canGetBePresentUntil,
      canGetFirstAttackAt,
      canGetSeenFirstTimeBy,
      groupName,
    }),
    [
      canGetAttackedAt,
      canGetAttackedBy,
      canGetBePresentUntil,
      canGetFirstAttackAt,
      canGetSeenFirstTimeBy,
      groupName,
    ],
  );
  const { data, loading, refetch, client } = useQuery(GET_TOE_PORTS, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    onCompleted: (): void => {
      addAuditEvent("Group.ToePorts", groupName);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load toe ports", error);
      });
    },
    variables: {
      ...getToePortsVariables,
      filters: toePortsFilters,
      first: 150,
      sortBy: tableHelpers.sortBy as unknown as ToePortsSortInput,
    },
  });
  const toePorts = useMemo((): IToePortData[] => {
    const toePortsEdges = data === undefined ? [] : data.group.toePorts.edges;

    return (toePortsEdges ?? [])
      .filter(
        (edge): edge is ToePortEdge & { node: IToePortData } =>
          edge?.node !== null,
      )
      .map(({ node }): IToePortData => formatToePortData(node as IToePortAttr));
  }, [data]);

  const storeUpdatedPort = useCallback(
    (
      rootId: string,
      address: string,
      port: number,
      updatedToePort: IToePortAttr,
    ): void => {
      if (data?.group) {
        client.writeQuery({
          data: {
            ...data,
            group: {
              ...data.group,
              toePorts: {
                ...data.group.toePorts,
                edges: (data.group.toePorts.edges ?? [])
                  .filter((value): value is IToePortEdge => value !== null)
                  .map(
                    (value): IToePortEdge =>
                      value.node.address === address &&
                      value.node.port === port &&
                      isEqualRootId(value.node.root, rootId)
                        ? {
                            node: updatedToePort,
                          }
                        : {
                            node: value.node,
                          },
                  ),
              },
            },
          },
          query: GET_TOE_PORTS,
          variables: { ...getToePortsVariables, first: 150 },
        });
      }
    },
    [client, data, getToePortsVariables],
  );

  const handleUpdateToePortBePresent = useCallback(
    async (row: IToePortData): Promise<void> => {
      const { rootId, address, port, bePresent } = row;

      const result = await handleUpdateToePort({
        variables: {
          ...getToePortsVariables,
          address,
          bePresent: !bePresent,
          groupName,
          hasRecentAttack: undefined,
          port,
          rootId,
          shouldGetNewToePort: true,
        },
      });

      if (!isNil(result.data) && result.data.updateToePort.success) {
        const updatedToePort = result.data.updateToePort
          .toePort as IToePortAttr;
        if (!isUndefined(updatedToePort)) {
          storeUpdatedPort(rootId, address, port, updatedToePort);
        }
        msgSuccess(
          t("group.toe.ports.alerts.updatePort"),
          t("groupAlerts.updatedTitle"),
        );
      }
    },
    [getToePortsVariables, groupName, handleUpdateToePort, storeUpdatedPort, t],
  );
  const toggleAdd = useCallback((): void => {
    additionModalProps.open();
  }, [additionModalProps]);

  const size = data?.group.toePorts.total;
  const tableColumns = getColumns(
    canUpdateToePort,
    isInternal,
    getToePortsVariables,
    handleUpdateToePortBePresent,
  );
  const enabledRows = useCallback((row: Row<IToePortData>): boolean => {
    return row.original.bePresent;
  }, []);
  const handleSearch = useDebouncedCallback((search: string): void => {
    void refetch({ filters: { rootNickname: search } });
  }, DEBOUNCE_DELAY);

  const { table } = useTable({
    columns: tableColumns,
    data: filteredDataset,
    filters: tableHelpers as unknown as TStore<IToePortData, ToePortsSort>,
    id: "toe_ports_table",
    options: {
      actionsOptions: { onSearch: handleSearch },
      columnOptions: {
        hidden: [
          "address",
          "be_present",
          "attacked_by",
          "first_attack_at",
          "be_present_until",
        ],
        order: [
          "root",
          "port",
          "has_vulnerabilities",
          "seen_at",
          "attacked_at",
          "seen_first_time_by",
        ],
        pinned: ["root"],
      },
      loading,
      rowOptions: {
        enableMultiSelection: isInternal && canUpdateToePort,
        validator: enabledRows,
      },
    },
    total: size ?? 0,
  });
  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedToePorts: IToePortData[] = Object.keys(rowSelection).map(
    (key): IToePortData => tableData?.get(key) ?? ({} as IToePortData),
  );

  const handleOnMarkAsAttackedCompleted = useCallback(
    (result: FetchResult<UpdateToePortMutation>): void => {
      if (!isNil(result.data) && result.data.updateToePort.success) {
        msgSuccess(
          t("group.toe.ports.alerts.markAsAttacked.success"),
          t("groupAlerts.updatedTitle"),
        );
        void refetch();
        table.setRowSelection({});
      }
    },
    [refetch, t, table],
  );

  const handleMarkAsAttacked = useCallback(async (): Promise<void> => {
    const presentSelectedToePortData = selectedToePorts.filter(
      (toePortData): boolean => toePortData.bePresent,
    );
    setIsMarkingAsAttacked(true);
    const results = await Promise.all(
      presentSelectedToePortData.map(
        async (toePortData): Promise<FetchResult<UpdateToePortMutation>> =>
          handleUpdateToePort({
            variables: {
              ...getToePortsVariables,
              address: toePortData.address,
              bePresent: toePortData.bePresent,
              groupName,
              hasRecentAttack: true,
              port: toePortData.port,
              rootId: toePortData.rootId,
              shouldGetNewToePort: false,
            },
          }),
      ),
    );
    const errors = getErrors<UpdateToePortMutation>(results);

    if (!isEmpty(results) && isEmpty(errors)) {
      handleOnMarkAsAttackedCompleted(results[0]);
    } else {
      void refetch();
    }
    setIsMarkingAsAttacked(false);
  }, [
    getToePortsVariables,
    groupName,
    handleOnMarkAsAttackedCompleted,
    handleUpdateToePort,
    refetch,
    selectedToePorts,
  ]);

  const { Filters, options, removeFilter } = useToEPortsFilters({
    dataset: toePorts,
    groupName,
    setBackFilters: setToePortsFilters,
    setFilteredDataset,
  });

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Table.Columns table={table} />
            <Container gap={0.5} id={"actions-container"}>
              <ActionButtons
                arePortsSelected={selectedToePorts.length > 0}
                isAdding={additionModalProps.isOpen}
                isInternal={isInternal}
                isMarkingAsAttacked={isMarkingAsAttacked}
                onAdd={toggleAdd}
                onMarkAsAttacked={handleMarkAsAttacked}
              />
              <Table.Export csvConfig={{ export: true }} data={filteredDataset}>
                <Button
                  icon={"file-export"}
                  id={"CSVExportBtn"}
                  tooltip={t("buttons.exportTip", { format: "CSV" })}
                  variant={"ghost"}
                >
                  {t("group.findings.exportCsv.text")}
                </Button>
              </Table.Export>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      <AdditionModal
        groupName={groupName}
        modalRef={additionModalProps}
        refetchData={refetch}
      />
    </StrictMode>
  );
};

export { GroupToePorts };
